import { useEffect, useState } from "react"
import { IdToken, useAuth0 } from "@auth0/auth0-react"
import { jwtDecode } from "jwt-decode"

import { ROLE_FUTURO, ROLE_CASSADIGITALE, ROLE_MEDIATORI } from "../consts/roles"
import { getAccessTokenSilentlyWithLock, handleAuth0RecoverableError } from "../utils/auth0Errors"

export type TokenData = {
  userToken: string
  email: string
  userId: string | undefined
  companyId: string | undefined
  companyVatCode: string
  companyName: string
  companyAddress: string
  firstName: string
  lastName: string
  roleFuturo: boolean
  roleCassaDigitale: boolean
  roleMediatori: boolean
}

const testData: TokenData = {
  userToken: "1234567890098765432asdfghjklpoiuytrewq",
  email: "test@test.it",
  userId: "userId",
  companyId: "companyId",
  companyVatCode: "00000000000",
  companyName: "companyName",
  companyAddress: "companyAddress",
  firstName: "firstName",
  lastName: "lastName",
  roleFuturo: true,
  roleCassaDigitale: false,
  roleMediatori: false
}

type TokenDataResponse = {
  data: TokenData
  accessToken: string | undefined
  error: Error | undefined
  isLoading: boolean
}

export const useTokenData = (): TokenDataResponse => {
  const { getIdTokenClaims, getAccessTokenSilently, loginWithRedirect, error: auth0Error, isLoading } = useAuth0()
  const [userAccessToken, setUserAccessToken] = useState<string>("")
  const [userIdToken, setUserIdToken] = useState<IdToken | undefined>(undefined)
  const [userData, setUserData] = useState<TokenData>({} as TokenData)

  useEffect(() => {
    let ignore = false
    const getToken = async () => {
      try {
        const [token, claims] = await Promise.all([
          getAccessTokenSilentlyWithLock(getAccessTokenSilently),
          getIdTokenClaims()
        ])

        if (ignore) return
        setUserAccessToken(token || "")
        setUserIdToken(claims || undefined)
      } catch (tokenError) {
        const handled = await handleAuth0RecoverableError(tokenError, loginWithRedirect)
        if (!handled) {
          console.error("Unable to retrieve Auth0 tokens", tokenError)
        }
      }
    }

    if (isLoading || ignore) return
    void getToken()

    return () => {
      ignore = true
    }
  }, [isLoading, setUserAccessToken, setUserIdToken, getAccessTokenSilently, getIdTokenClaims, loginWithRedirect])

  useEffect(() => {
    if (userAccessToken && userIdToken) {
      const decodedToken: Record<string, string> = jwtDecode(userAccessToken)

      if (decodedToken) {
        const roles = decodedToken["https://credimi.com/roles"]
        const data = {
          userToken: userAccessToken,
          email: decodedToken["https://credimi.com/username"],
          userId: decodedToken["https://credimi.com/userId"],
          companyId: decodedToken["https://credimi.com/companyId"],
          companyVatCode: decodedToken["https://credimi.com/companyVatCode"],
          companyName: userIdToken["https://credimi.com/companyName"],
          companyAddress: userIdToken["https://credimi.com/companyAddress"],
          firstName: userIdToken["https://credimi.com/firstName"],
          lastName: userIdToken["https://credimi.com/lastName"],
          roleFuturo: roles.includes(ROLE_FUTURO) ?? false,
          roleCassaDigitale: roles.includes(ROLE_CASSADIGITALE) ?? false,
          roleMediatori: roles.includes(ROLE_MEDIATORI) ?? false
        }

        setUserData(data)
      }
    }
  }, [userAccessToken, userIdToken])

  if (process.env.NODE_ENV === "test") {
    return { data: testData, accessToken: undefined, error: undefined, isLoading: false }
  }

  return { data: userData, accessToken: userAccessToken, error: auth0Error, isLoading }
}
